/**
 * Vista de impresión de Remisión (formato AMX-VTA-FR-06_R2)
 *
 * Patrón alineado a PrintEntrega.js (FR-07):
 * - render() retorna un loader.
 * - afterRender() hace el fetch, llama renderPrint(), fitToViewport() y auto-imprime.
 * - generateHTML(r) es función pura exportable (reusable para PDF en el futuro).
 */

import remisionService from '../services/remision.service.js';
import router from '../router/router.js';

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const fmt = (n, decimals = 2) => {
  if (n === null || n === undefined || isNaN(Number(n))) return decimals === 4 ? '0.0000' : '0.00';
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const fmtFecha = (f) => {
  if (!f) return '';
  const [y, m, d] = String(f).substring(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : '';
};

// Convertidor número → letras en español (formato fiscal mexicano)
// Ej: 43000.80, 'USD' → "CUARENTA Y TRES MIL DOLARES 80/100 USD"
function numeroALetras(num, moneda = 'MXN') {
  if (num === null || num === undefined) return '';
  const n = Number(num);
  if (isNaN(n)) return '';
  const entero = Math.floor(Math.abs(n));
  const centavos = Math.round((Math.abs(n) - entero) * 100);
  const palabras = enteroALetras(entero);
  const monedaTxt = moneda === 'USD'
    ? (entero === 1 ? 'DOLAR' : 'DOLARES')
    : (entero === 1 ? 'PESO' : 'PESOS');
  const sufijoMoneda = moneda === 'USD' ? ' USD' : ' M.N.';
  const cents = String(centavos).padStart(2, '0');
  const signo = n < 0 ? 'MENOS ' : '';
  return `${signo}${palabras} ${monedaTxt} ${cents}/100${sufijoMoneda}`.toUpperCase();
}

function enteroALetras(num) {
  if (num === 0) return 'CERO';
  if (num < 0) return 'MENOS ' + enteroALetras(-num);

  const UNIDADES = ['', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
  const ESPECIALES_10 = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISEIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
  const DECENAS = ['', '', 'VEINTI', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
  const CENTENAS = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];

  function hasta999(n) {
    if (n === 0) return '';
    if (n === 100) return 'CIEN';
    let r = '';
    const c = Math.floor(n / 100);
    const resto = n % 100;
    if (c > 0) r += CENTENAS[c];
    if (resto > 0) {
      if (r) r += ' ';
      if (resto < 10) r += UNIDADES[resto];
      else if (resto < 20) r += ESPECIALES_10[resto - 10];
      else if (resto < 30) {
        const u = resto % 10;
        r += DECENAS[2] + (u > 0 ? UNIDADES[u].toLowerCase() : '');
        if (resto === 20) r = r.replace(/I$/, '') + 'NTE';
      } else {
        const d = Math.floor(resto / 10);
        const u = resto % 10;
        r += DECENAS[d];
        if (u > 0) r += ' Y ' + UNIDADES[u];
      }
    }
    return r.toUpperCase();
  }

  let resultado = '';
  const millones = Math.floor(num / 1000000);
  const miles = Math.floor((num % 1000000) / 1000);
  const resto = num % 1000;

  if (millones > 0) {
    resultado += millones === 1 ? 'UN MILLON' : (hasta999(millones) + ' MILLONES');
  }
  if (miles > 0) {
    if (resultado) resultado += ' ';
    resultado += miles === 1 ? 'MIL' : (hasta999(miles) + ' MIL');
  }
  if (resto > 0) {
    if (resultado) resultado += ' ';
    resultado += hasta999(resto);
  }
  return resultado.toUpperCase();
}

export default {
  data: { remision: null },

  async render() {
    return `<div id="printContent" style="text-align:center;padding:2rem;">Cargando formato de remisión...</div>`;
  },

  async afterRender(params) {
    window.currentView = this;
    try {
      const res = await remisionService.obtenerPorId(params.id);
      this.data.remision = res?.data || res;
      // Define el filename por defecto al guardar como PDF (Edge/Chrome usan document.title).
      if (this.data.remision?.numero_remision) {
        document.title = `Remision_${this.data.remision.numero_remision}`;
      }
      this.renderPrint(this.data.remision);
      this.fitToViewport();
      setTimeout(() => window.print(), 500);
    } catch (err) {
      const c = document.getElementById('content');
      if (c) c.innerHTML = `<div style="padding:2rem;text-align:center;color:var(--danger);">Remisión no encontrada</div>`;
    }
  },

  renderPrint(r) {
    document.getElementById('content').innerHTML = this.generateHTML(r);
  },

  fitToViewport() {
    const container = document.querySelector('.rem-print');
    if (!container) return;
    const pageWidth = 850; // ancho fijo del documento
    const vw = document.documentElement.clientWidth;
    if (vw < pageWidth) {
      container.style.zoom = vw / pageWidth;
    }
  },

  generateHTML(r) {
    if (!r) return '';
    // Si la remisión ya tiene total_letras guardado (legacy), úsalo;
    // si no, lo computamos automáticamente desde total + moneda.
    const totalEnLetras = r.total_letras || numeroALetras(r.total, r.moneda);

    // Texto legal por default si la remisión no lo guardó (igual al del form)
    const CONDICIONES_DEFAULT = `El cliente se compromete a cuidar la mercancía que se le entregue y hacerse responsable por cualquier daño ocasionado. El cliente se compromete a devolver a Agromx la mercancía no vendida en condiciones óptimas. El cliente deberá entregar a Agromx la cantidad monetaria descrita en la presente remisión cuando la mercancía sea vendida o no se encuentre en las instalaciones del cliente. El cliente deberá permitir a personal de Agromx realizar revisiones periódicas de la mercancía amparada en la presente remisión. Agromx se reserva el dominio de la mercancía hasta su liquidación de lo contrario la pondrá a disposición de Agromx en un lapso no mayor a 2 días laborales. Si la mercancía fuera vendida por el cliente o no estuviera en sus instalaciones y no entregara el pago monetario descrito en la remisión el cliente se obliga a pagar el 4 % mensual por concepto de interés moratorio.`;
    const condiciones = r.condiciones_venta || CONDICIONES_DEFAULT;

    return `
      <style>
        @page { size: portrait; margin: 5mm; }
        @media print {
          body, html { margin: 0; padding: 0; background: white; }
          .sidebar, .navbar, nav, #sidebar, #navbarUser, .navbar-container { display: none !important; }
          .main-content, .main-layout, #app, #content {
            margin-left: 0 !important;
            padding: 0 !important;
            max-width: 100% !important;
            display: block !important;
          }
          .no-print { display: none !important; }
          .rem-print { box-shadow: none !important; zoom: 1 !important; }
        }

        .rem-print {
          font-family: Arial, sans-serif;
          background: white;
          color: #111;
          max-width: 850px;
          margin: 1rem auto;
          padding: 1rem 2rem;
          font-size: 12px;
          box-shadow: 0 0 8px rgba(0,0,0,0.1);
        }
        .rem-print * { box-sizing: border-box; }
        .rem-print h2 { color: #1d4ed8; margin: 0 0 0.5rem; font-size: 16px; }
        .rem-print .row { display: flex; gap: 1rem; }
        .rem-print .col { flex: 1; }
        .rem-print table { width: 100%; border-collapse: collapse; }
        .rem-print .header-table td {
          padding: 0.2rem 0.6rem;
          background: #A2DBA3;
          border: 1px solid #6abf7f;
          font-size: 11px;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        .rem-print .data-table {
          margin-top: 0.5rem;
        }
        .rem-print .data-table th {
          background: #A2DBA3;
          font-weight: bold;
          padding: 0.2rem;
          font-size: 9.5px;
          border: 1px solid #6abf7f;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        .rem-print .data-table td {
          padding: 0.3rem 0.4rem;
          border: 1px solid #6abf7f;
          font-size: 9.5px;
          vertical-align: top;
        }
        .rem-print .footer-section { margin-top: 1.5rem; }
        .rem-print .legal-text {
          font-size: 9px;
          color: #444;
          line-height: 1.4;
          padding: 0.5rem;
          border: 1px solid #6abf7f;
          background: #f7fdf8;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        .rem-print .totales-table { float: right; width: 220px; }
        .rem-print .totales-table td { padding: 0.18rem 0.5rem; border: 1px solid #6abf7f; }
        .rem-print .totales-table td.label {
          background: #A2DBA3;
          font-weight: bold;
          text-align: right;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
        .rem-print .totales-table td.value { text-align: right; font-weight: 600; }
        .rem-print .recibe-box {
          width: 100%;
          box-sizing: border-box;
          padding: 0.5rem 0.5rem 0.1rem;
          font-size: 11px;
        }
        .rem-print .recibe-box .field { margin: 0.4rem 0; border-bottom: 1px solid #999; padding: 0.2rem 0; }
        .rem-print .recibe-box img { max-height: 50px; }
        .rem-print .footer-codigo {
          text-align: center;
          margin-top: 0.25rem;
          font-size: 10px;
          color: #555;
          padding-top: 0;
        }
        .rem-print .estado-watermark {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-30deg);
          font-size: 100px;
          color: rgba(220, 38, 38, 0.15);
          font-weight: 900;
          pointer-events: none;
          z-index: 0;
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
      </style>

      <div class="rem-print">
        ${r.estado !== 'emitida' ? `<div class="estado-watermark">${esc(r.estado).toUpperCase()}</div>` : ''}

        <!-- Encabezado -->
        <div class="row" style="align-items:center;margin-bottom:1rem;">
          <img src="/img/Logo_AGRO.png" alt="AGROMX" style="height:55px;width:auto;" onerror="this.style.display='none'" />
          <div style="flex:1;text-align:right;">
            <div style="font-weight:bold;font-size:14px;">AGROMX EQUIPOS ESPECIALES S.A.P.I. DE C.V.</div>
            <div style="font-size:11px;color:#444;">SOLUCIONES INTEGRALES PARA EL SECTOR AGROPECUARIO</div>
          </div>
        </div>

        <!-- Cliente (izquierda) + Datos del documento (derecha) — 50/50 -->
        <div style="display:flex;gap:0.5rem;align-items:flex-start;">
          <table class="header-table" style="flex:1;">
            <tr>
              <td style="font-weight:bold;">Cliente:</td>
            </tr>
            <tr>
              <td style="background:white;border:1px solid #6abf7f;font-weight:normal;font-size:11px;vertical-align:top;line-height:1.4;height:73px;">
                ${esc(r.cliente_nombre || '')}${r.cliente_rfc ? `, RFC: ${esc(r.cliente_rfc)}` : ''}
              </td>
            </tr>
          </table>

          <table class="header-table" style="flex:1;">
            <tr>
              <td style="font-weight:bold;width:45%;">REMISIÓN No. :</td>
              <td style="background:white;text-align:center;font-weight:bold;">${esc(r.numero_remision)}</td>
            </tr>
            <tr>
              <td style="font-weight:bold;">Fecha</td>
              <td style="background:white;text-align:center;">${fmtFecha(r.fecha_remision)}</td>
            </tr>
            <tr>
              <td style="font-weight:bold;">Vendedor:</td>
              <td style="background:white;text-align:center;">${esc(r.vendedor_nombre || '')}</td>
            </tr>
            <tr>
              <td style="font-weight:bold;">Forma de pago</td>
              <td style="background:white;text-align:center;">${(r.forma_pago || '').toUpperCase()}</td>
            </tr>
          </table>
        </div>

        <!-- Observaciones (debajo, ancho completo) -->
        <table class="header-table" style="margin-top:0.4rem;">
          <tr>
            <td style="font-weight:bold;">Observaciones:</td>
          </tr>
          <tr>
            <td style="background:white;border:none;font-weight:normal;font-size:11px;vertical-align:top;padding:0.3rem 0.5rem;height:32px;">
              ${esc(r.observaciones || '')}
            </td>
          </tr>
        </table>

        <!-- Tabla de productos -->
        <table class="data-table">
          <thead>
            <tr>
              <th style="width:80px;">Cantidad</th>
              <th style="width:80px;">UNIDAD</th>
              <th>CONCEPTO</th>
              <th style="width:130px;">MODELO</th>
              <th style="width:100px;">P/U</th>
              <th style="width:100px;">Importe</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="text-align:center;">${fmt(r.cantidad)}</td>
              <td style="text-align:center;">${esc(r.unidad || 'PZ')}</td>
              <td style="text-align:center;">
                ${esc(r.concepto || '')}
                <div style="text-align:left;margin-top:0.15rem;"><small>Numero de serie: <strong>${esc(r.num_serie)}</strong></small></div>
              </td>
              <td style="text-align:center;">
                ${esc(r.tractor_marca || r.marca || '')}<br/>
                ${esc(r.tractor_modelo || r.modelo || '')}
              </td>
              <td style="text-align:center;">${fmt(r.precio_unitario, 4)}</td>
              <td style="text-align:center;">${fmt(r.subtotal)}</td>
            </tr>
            <!-- Espacio vacío para matchear formato AMX-VTA-FR-06_R2 -->
            <tr>
              <td colspan="6" style="height:120px;border:none;"></td>
            </tr>
          </tbody>
        </table>

        <!-- Footer: lugar entrega, recibe, totales -->
        <div class="row footer-section">
          <div class="col">
            <table class="header-table" style="margin-bottom:0.5rem;">
              <tr><td style="text-align:center;font-weight:bold;">LUGAR DE ENTREGA</td></tr>
            </table>
            <div style="border:1px solid #6abf7f;padding:0.3rem 0.5rem;min-height:24px;font-size:11px;">
              ${esc(r.lugar_entrega || '')}
            </div>

            <table class="header-table" style="margin:0.5rem 0;">
              <tr><td style="text-align:center;font-weight:bold;">CONDICIONES DE VENTA</td></tr>
            </table>
            <div class="legal-text">${esc(condiciones)}</div>
          </div>

          <div class="col" style="flex:0 0 230px;max-width:230px;">
            <div style="text-align:center;font-weight:bold;font-size:11px;padding:0.2rem 0.6rem;">RECIBE</div>
            <div class="recibe-box" style="padding-top:0;">
              <div style="display:flex;align-items:flex-end;gap:0.4rem;margin:0.1rem 0 0.4rem;">
                <strong style="white-space:nowrap;">Nombre:</strong>
                <span style="flex:1;border-bottom:1px solid #999;padding:0 0.2rem;min-height:1.2rem;">${esc(r.recibe_nombre || '')}</span>
              </div>
              <div style="display:flex;align-items:flex-end;gap:0.4rem;margin:0.4rem 0;">
                <strong style="white-space:nowrap;">Fecha:</strong>
                <span style="flex:1;border-bottom:1px solid #999;padding:0 0.2rem;min-height:1.2rem;">${fmtFecha(r.recibe_fecha)}</span>
              </div>
              <div style="display:flex;align-items:flex-end;gap:0.4rem;margin:0.4rem 0;min-height:85px;">
                <strong style="white-space:nowrap;">Firma:</strong>
                <span style="flex:1;border-bottom:1px solid #999;padding:0 0.2rem;display:flex;justify-content:center;align-items:flex-end;">
                  ${r.recibe_firma_url
                    ? `<img class="firma-recibe-img" src="${esc(r.recibe_firma_url)}" style="max-height:80px;max-width:100%;object-fit:contain;" />`
                    : ''}
                </span>
              </div>
            </div>

            <table class="totales-table" style="float:none;width:100%;margin-top:0;">
              <tr><td class="label">Subtotal</td><td class="value">${fmt(r.subtotal)}</td></tr>
              <tr><td class="label">Descuento</td><td class="value">${fmt(r.descuento)}</td></tr>
              <tr><td class="label">I.V.A.</td><td class="value">${fmt(r.iva)}</td></tr>
              <tr><td class="label">Total</td><td class="value"><strong>${fmt(r.total)}</strong></td></tr>
            </table>
          </div>
        </div>

        <!-- Total en letras + footer institucional (agrupados para evitar split de página) -->
        <div style="page-break-inside:avoid;break-inside:avoid;">
        <div style="margin-top:1rem;padding:0.25rem;font-weight:600;font-size:12px;text-align:center;text-transform:uppercase;">
          ${esc(totalEnLetras)}
        </div>

        <!-- Footer institucional -->
        <div class="footer-codigo">
          <div style="font-size:8px;color:#666;font-style:italic;margin-bottom:0.5rem;">
            Los datos personales recabados serán protegidos, incorporados y tratados en el Sistema de Datos Personales correspondiente, de conformidad con lo dispuesto por la Ley Federal de
            Transparencia y Acceso a la Información Pública Gubernamental y demás disposiciones aplicables.
          </div>
          <div style="font-weight:700;font-size:12px;color:#111;margin:0.5rem 0;">SIN MAS POR EL MOMENTO</div>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:1rem;margin:0.5rem 0;">
            <div style="font-size:11px;font-weight:700;color:#111;text-align:center;flex:0 0 auto;line-height:1.3;">
              CODIGO<br>AMX-VTA-FR-06_R2
            </div>
            <div style="font-weight:700;font-size:12px;color:#111;text-align:center;flex:1;">
              AGROMX EQUIPOS ESPECIALES S.A.P.I. DE C.V.
            </div>
            <div style="font-size:11px;font-weight:700;color:#111;text-align:center;flex:0 0 auto;line-height:1.3;">
              VIGENCIA<br>JUNIO 2024 AL 2026
            </div>
          </div>
        </div>
        </div>
      </div>
    `;
  },

  volver() {
    if (this.data.remision?.id) {
      router.navigate(`/ventas/remisiones/${this.data.remision.id}`);
    } else {
      router.navigate('/ventas/remisiones');
    }
  }
};
